/**
 * lib/cashBookEntry.ts
 * ------------------------------------------------------------------
 * CASH BOOK CAPTURE — money in and out that belongs to no pupil and no supplier.
 *
 * Bank charges. Interest received. A refund from a supplier. A donation. The
 * municipal direct debit. None of these has an invoice or a voucher behind them,
 * and today the only way to record one is to write a general journal — which
 * means an accounts clerk cannot record a bank charge without knowing which side
 * of a double entry the bank goes on. Pastel gives them a cash book with two
 * columns; this is that.
 *
 * ONE SIDE IS ALWAYS THE BANK, which is what makes this safe for someone who
 * does not think in debits and credits. The clerk picks receipt or payment,
 * names the account it relates to, and the direction follows:
 *
 *   receipt   Dr bank / cash        Cr the account it came from
 *   payment   Dr the account        Cr bank / cash
 *
 * BATCHED, BUT POSTED ONE JOURNAL PER LINE. A clerk works from a bank statement
 * and enters a dozen at a time, so the screen is a grid. Each line posts its own
 * journal, though, because the bank reconciliation matches a statement line to a
 * ledger entry: one journal carrying twelve unrelated movements is a journal
 * that can never be ticked off against anything.
 *
 * PURE. Types, validation and the lines. No Firestore, no React.
 * ------------------------------------------------------------------ */
import type { AccountType, JournalLine } from '@/lib/gl';

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

export type CashEntryKind = 'receipt' | 'payment';

export const CASH_ENTRY_LABEL: Record<CashEntryKind, string> = {
  receipt: 'Money in',
  payment: 'Money out',
};

export const CASH_ENTRY_HELP: Record<CashEntryKind, string> = {
  receipt: 'Interest, a donation, a refund from a supplier — money arriving that no invoice covers.',
  payment: 'Bank charges, a direct debit, a small purchase — money leaving that no voucher covers.',
};

export interface CashEntryDraft {
  /** Row id, for the grid only. */
  key: string;
  kind: CashEntryKind;
  date: string;
  /** The bank or cash account the money moved through. */
  bankCode: string;
  /** The income or expense account it relates to. */
  contraCode: string;
  amount: number;
  reference: string;
  description: string;
}

export interface CashEntryIssue {
  key: string;
  message: string;
}

/**
 * What is wrong with a row, in the words of the person entering it.
 *
 * A row that is completely empty is not an error — a grid always has a spare
 * line at the bottom and complaining about it teaches people to ignore the
 * warnings that matter.
 */
export function validateEntry(
  e: CashEntryDraft,
  typeOf: (code: string) => AccountType | undefined,
): string | null {
  if (isBlank(e)) return null;
  if (!e.date) return 'No date.';
  if (!e.bankCode) return 'Choose which bank or cash account the money moved through.';
  if (!e.contraCode) return 'Choose the account this relates to.';
  if (e.bankCode === e.contraCode) return 'The bank account and the account it relates to are the same.';
  if (!(round2(e.amount) > 0)) return 'Enter an amount greater than nil.';
  if (!e.description.trim()) return 'Say what it was for.';

  // A cash-book line whose other side is another bank account is a TRANSFER, and
  // recording it here would show the school earning income every time it moved
  // its own money between accounts.
  const t = typeOf(e.contraCode);
  if (t === 'asset' && /bank|cash/i.test(e.contraCode)) {
    return 'That looks like another bank account — use Transfer rather than a receipt or payment.';
  }
  return null;
}

export function isBlank(e: CashEntryDraft): boolean {
  return !e.contraCode && !round2(e.amount) && !e.reference.trim() && !e.description.trim();
}

export function entryIssues(
  rows: CashEntryDraft[],
  typeOf: (code: string) => AccountType | undefined,
): CashEntryIssue[] {
  return rows
    .map((r) => ({ key: r.key, message: validateEntry(r, typeOf) }))
    .filter((x): x is CashEntryIssue => !!x.message);
}

/** The two lines one entry posts. The bank side is never the one the clerk
 *  has to reason about — see the note at the top. */
export function cashEntryLines(e: CashEntryDraft): JournalLine[] {
  const amount = round2(e.amount);
  return e.kind === 'receipt'
    ? [
      { accountCode: e.bankCode, dr: amount, cr: 0, narration: e.reference || e.description },
      { accountCode: e.contraCode, dr: 0, cr: amount, narration: e.description },
    ]
    : [
      { accountCode: e.contraCode, dr: amount, cr: 0, narration: e.description },
      { accountCode: e.bankCode, dr: 0, cr: amount, narration: e.reference || e.description },
    ];
}

export interface BatchTotals {
  receipts: number;
  payments: number;
  net: number;
  rows: number;
}

export function batchTotals(rows: CashEntryDraft[]): BatchTotals {
  const live = rows.filter((r) => !isBlank(r));
  const receipts = round2(live.filter((r) => r.kind === 'receipt').reduce((n, r) => n + round2(r.amount), 0));
  const payments = round2(live.filter((r) => r.kind === 'payment').reduce((n, r) => n + round2(r.amount), 0));
  return { receipts, payments, net: round2(receipts - payments), rows: live.length };
}

export const emptyEntry = (kind: CashEntryKind, bankCode: string, date: string): CashEntryDraft => ({
  key: `r${Math.random().toString(36).slice(2, 9)}`,
  kind,
  date,
  bankCode,
  contraCode: '',
  amount: 0,
  reference: '',
  description: '',
});
